import React, { useState, useEffect, useRef } from 'react';
import { MapPin, Navigation, ExternalLink, ChevronDown, ChevronUp, Star, Clock } from 'lucide-react';

const PURPLE = '#5B2D8E';

const getCategoryLabel = (cat) => {
  const labels = {
    food: 'Food & Drink', art: 'Art', architecture: 'Architecture',
    museum: 'Museum', viewpoint: 'Point of Interest', cultural_venue: 'Cultural Venue',
    hidden_gem: 'Hidden Gem', shop: 'Shop', highlight: 'Highlight',
  };
  return labels[cat] || cat || 'Stop';
};

const getCategoryColor = (cat) => {
  const colors = {
    food: '#6D28D9', art: '#7C3AED', architecture: '#DC2626',
    museum: '#2563EB', viewpoint: '#0D9488', cultural_venue: '#9333EA',
    hidden_gem: '#F59E0B', shop: '#1E40AF',
  };
  return colors[cat] || '#6b7280';
};

const RouteTimeline = ({ waypoints = [], walk }) => {
  const [expanded, setExpanded] = useState({ 0: true });
  const [activeIndex, setActiveIndex] = useState(0);
  const stopRefs = useRef([]);

  const sorted = [...waypoints].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  // Intersection observer for scroll-based highlighting
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const idx = parseInt(entry.target.dataset.stopIndex, 10);
            if (!isNaN(idx)) setActiveIndex(idx);
          }
        });
      },
      { rootMargin: '-30% 0px -60% 0px', threshold: 0 }
    );

    stopRefs.current.forEach(ref => { if (ref) observer.observe(ref); });
    return () => observer.disconnect();
  }, [sorted.length]);

  const toggle = (i) => setExpanded(prev => ({ ...prev, [i]: !prev[i] }));

  const scrollToStop = (i) => {
    stopRefs.current[i]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setExpanded(prev => ({ ...prev, [i]: true }));
  };

  const getDirectionsUrl = (wp) => {
    const lat = wp.coords?.lat;
    const lng = wp.coords?.lng;
    if (lat && lng && lat !== 0 && lng !== 0) {
      return `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}&travelmode=walking`;
    }
    return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(wp.address || wp.name)}&travelmode=walking`;
  };

  if (sorted.length === 0) return null;

  return (
    <div>
      {/* Section header */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 tracking-tight">Route Stops</h2>
        <p className="text-gray-500 mt-1 text-[15px]">
          {sorted.length} stops along your walk
        </p>
      </div>

      {/* Timeline */}
      <div className="relative">
        {/* Vertical line */}
        <div
          className="absolute left-5 top-6 bottom-6 w-0.5"
          style={{ backgroundColor: `${PURPLE}30` }}
        />

        <div className="space-y-0">
          {sorted.map((wp, i) => {
            const isActive = i === activeIndex;
            const isOpen = expanded[i];
            const cat = wp.category || 'viewpoint';

            return (
              <div
                key={i}
                ref={el => { stopRefs.current[i] = el; }}
                data-stop-index={i}
                className="relative pl-14 pb-6"
              >
                {/* Dot on timeline */}
                <div
                  className="absolute left-3 top-3 rounded-full border-2 transition-all duration-200"
                  style={{
                    width: isActive ? 18 : 14,
                    height: isActive ? 18 : 14,
                    backgroundColor: isActive ? PURPLE : '#e5e7eb',
                    borderColor: isActive ? PURPLE : '#d1d5db',
                    marginLeft: isActive ? -2 : 0,
                    marginTop: isActive ? -2 : 0,
                  }}
                />

                {/* Card */}
                <div
                  className={`rounded-xl border bg-white transition-all duration-200 ${
                    isActive ? 'border-purple-300 shadow-md' : 'border-gray-200 shadow-sm'
                  }`}
                >
                  {/* Card header — always visible */}
                  <button
                    onClick={() => toggle(i)}
                    className="w-full text-left px-4 py-3 flex items-center gap-3"
                  >
                    {/* Stop number */}
                    <span
                      className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold"
                      style={{ backgroundColor: PURPLE }}
                    >
                      {i + 1}
                    </span>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold text-gray-900 text-sm">{wp.name || `Stop ${i + 1}`}</h3>
                        <span
                          className="text-[10px] font-semibold px-2 py-0.5 rounded-full text-white"
                          style={{ backgroundColor: getCategoryColor(cat) }}
                        >
                          {getCategoryLabel(cat)}
                        </span>
                        {wp.user_ratings >= 4.0 && (
                          <span className="inline-flex items-center gap-0.5 text-xs text-gray-500">
                            <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                            {wp.user_ratings.toFixed(1)}
                          </span>
                        )}
                      </div>
                      {wp.address && (
                        <p className="text-xs text-gray-400 mt-0.5 truncate">{wp.address}</p>
                      )}
                    </div>

                    {isOpen
                      ? <ChevronUp className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      : <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    }
                  </button>

                  {/* Expanded content */}
                  {isOpen && (
                    <div className="px-4 pb-4 pt-0 border-t border-gray-100">
                      {wp.description && (
                        <p className="text-sm text-gray-600 leading-relaxed mt-3">
                          {wp.description}
                        </p>
                      )}

                      <div className="flex items-center gap-2 mt-3">
                        <a
                          href={getDirectionsUrl(wp)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-amber-500 hover:bg-amber-600 text-white text-xs font-bold rounded-lg transition-colors"
                        >
                          <Navigation className="w-3 h-3" />
                          Get Walking Directions
                        </a>
                        {wp.address && (
                          <a
                            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(wp.address)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 px-3 py-1.5 border border-gray-200 text-gray-600 text-xs font-medium rounded-lg hover:bg-gray-50 transition-colors"
                          >
                            <ExternalLink className="w-3 h-3" />
                            Google Maps
                          </a>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// Compact sidebar index for timeline mode
export const RouteTimelineIndex = ({ waypoints = [], activeIndex = 0, onStopClick }) => {
  const sorted = [...waypoints].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  if (sorted.length === 0) return null;

  return (
    <div className="space-y-1">
      {sorted.map((wp, i) => (
        <button
          key={i}
          onClick={() => onStopClick(i)}
          className={`w-full text-left flex items-center gap-2 px-2 py-1.5 rounded-md transition-colors text-sm ${
            i === activeIndex ? 'bg-purple-50 text-purple-800' : 'hover:bg-gray-50 text-gray-600'
          }`}
        >
          <span
            className="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-white text-[10px] font-bold"
            style={{ backgroundColor: i === activeIndex ? PURPLE : '#9ca3af' }}
          >
            {i + 1}
          </span>
          <span className="truncate">{wp.name || `Stop ${i + 1}`}</span>
        </button>
      ))}
    </div>
  );
};

export default RouteTimeline;
